import * as React from 'react';
import { FeatureTour, FeatureTourStep } from '../../components/featureTour/FeatureTour';
import {
    isFeatureTourOpen,
    recordFeatureTourAutoShown,
    recordFeatureTourClosed,
    shouldAutoStartFeatureTour,
} from '../../components/featureTour/featureTourState';

export const ENQUIRY_FORM_TOUR_ID = 'enquiry-form';
// Bump to show the tour again to everyone (after a big change to the form).
export const ENQUIRY_FORM_TOUR_VERSION = 2;

// The tour is for the three-column screen of an existing enquiry (a wide window): Details on the left, Activity in the middle, Follow Up or Matter on the
// right. Every step points at something on it by a data-tour label or a panel's own id. The form shows different things to different people (a user
// may not have the matter or follow-up access, a firm may have no Further Details), so a step whose target is not on the screen is skipped by the
// tour itself, and the wording that depends on the screen is read from it when the tour starts: nobody is told about something they cannot see.
//
// The form has two separate saves, and the tour keeps them apart: Save (bottom of the page) keeps changes to the enquiry's details, and the follow-up
// has its own Save follow-up inside its own section.
const getColumnTitle = (columnLabel: string): string => {
    const heading = document.querySelector(`[data-tour="${columnLabel}"]`);
    return heading && heading.textContent ? heading.textContent.trim() : '';
};

const FURTHER_DETAILS_TAB = '[data-tour="enquiry-column-middle"] [role="tab"]:nth-child(2)';

// The sections of the Details column. The "open a section" card points at one that is closed right now (the Enquiry Value section if it is, as it
// is often the one nobody has opened), found and marked when the tour starts; the mark comes off when the tour ends.
const SECTION_HEADERS = [
    'enquiry-value-header', 'digittaltracking-header', 'risk-assessed-header', 'documents-header', 'other-side-header',
    'organisation-header', 'detailed-enquiry-notes-header', 'enquiry-details-header', 'contact-details-header',
];
const OPEN_SECTION_MARK = 'enquiry-open-section';

const clearSectionMark = () => {
    document.querySelectorAll(`[data-tour="${OPEN_SECTION_MARK}"]`).forEach((element) => element.removeAttribute('data-tour'));
};

const markClosedSection = () => {
    clearSectionMark();
    for (const headerId of SECTION_HEADERS) {
        const header = document.getElementById(headerId);
        if (header && header.getAttribute('aria-expanded') === 'false') {
            header.setAttribute('data-tour', OPEN_SECTION_MARK);
            return;
        }
    }
};

// The Assignment step opens the section when it is closed, so its fields can be seen, and puts it back as the tour moves on or ends. Only a section
// the tour opened is closed again: one the user had open stays open.
const ASSIGNMENT_TARGET = '[data-tour="enquiry-assignment"]';
let isAssignmentOpenedByTour = false;

const openAssignmentSection = () => {
    const header = document.getElementById('assignment-header');
    if (header && header.getAttribute('aria-expanded') === 'false') {
        isAssignmentOpenedByTour = true;
        header.click();
    }
};

export const restoreAssignmentSection = () => {
    const header = document.getElementById('assignment-header');
    if (isAssignmentOpenedByTour && header && header.getAttribute('aria-expanded') === 'true') {
        header.click();
    }
    isAssignmentOpenedByTour = false;
};

// The Documents step works the same way: the section is opened so the drop box and the file list can be seen, and put back when the tour moves on or
// ends, unless the user already had it open.
const DOCUMENTS_TARGET = '[data-tour="enquiry-documents"]';
let isDocumentsOpenedByTour = false;

const openDocumentsSection = () => {
    const header = document.getElementById('documents-header');
    if (header && header.getAttribute('aria-expanded') === 'false') {
        isDocumentsOpenedByTour = true;
        header.click();
    }
};

export const restoreDocumentsSection = () => {
    const header = document.getElementById('documents-header');
    if (isDocumentsOpenedByTour && header && header.getAttribute('aria-expanded') === 'true') {
        header.click();
    }
    isDocumentsOpenedByTour = false;
};

// The names of the chips in an area of the form, as the user sees them (they are set up by the firm, so the cards read them instead of listing them).
const getChipLabels = (areaSelector: string, limit: number): string[] =>
    Array.from(document.querySelectorAll(`${areaSelector} .MuiChip-label`))
        .map((chip) => (chip.textContent || '').trim())
        .filter((label) => label.length > 0 && label.toLowerCase() !== 'show all')
        .slice(0, limit);

const joinBold = (labels: string[]) => labels.map((label, index) => (
    <React.Fragment key={label}>{index > 0 ? ', ' : ''}<b>{label}</b></React.Fragment>
));

export const createEnquiryFormTour = (): FeatureTourStep[] => {
    // the right-hand column is "Matter" once the enquiry has a matter, "Follow Up" until then
    const isThirdColumnMatter = getColumnTitle('enquiry-column-third').toLowerCase().indexOf('matter') === 0;
    // The bar under the history holds the create-client / matter buttons the user has access to, and for this enquiry at this stage; with none it is
    // an empty strip, so the step is left out, and when there are some it names the ones that are there.
    const convertButtonLabels = Array.from(document.querySelectorAll('[data-tour="enquiry-convert"] button'))
        .map((button) => (button.textContent || '').trim())
        .filter((label) => label.length > 0);

    // The Assignment section is greyed out (and cannot be opened) for someone who may not assign this enquiry, so it is only pointed at when it can be used.
    const assignmentHeader = document.getElementById('assignment-header');
    const canAssign = !!assignmentHeader && assignmentHeader.getAttribute('aria-disabled') !== 'true';

    markClosedSection();
    const logLabels = getChipLabels('[data-tour="enquiry-log-activity"]', 6);
    const actionLabels = getChipLabels('[data-tour="enquiry-do-something"]', 6);

    const steps: FeatureTourStep[] = [
        {
            badge: 'New',
            title: 'Your enquiry form',
            body: <>The enquiry is laid out in <b>three columns</b>: its details on the left, what has happened in the middle, and what happens next on the right. It takes under a minute.</>,
        },
        {
            target: '[data-tour="enquiry-badges"]',
            badge: 'Rate it',
            title: 'Quality and likelihood',
            body: <>Give the enquiry a <b>quality</b> and a <b>likelihood to proceed</b> rating with the stars. The area of law sits beside them.</>,
            tip: 'You can filter the Enquiries list by them.',
        },
        {
            target: '[data-tour="enquiry-journey"]',
            badge: 'Where it is up to',
            title: 'Next follow-up and journey',
            body: <>The <b>next follow-up</b> that is due and the journey so far: from the first contact to where the enquiry is now.</>,
        },
        {
            target: '[data-tour="enquiry-column-details"]',
            badge: 'Left · Details',
            title: 'The enquiry\'s details',
            body: <>Everything about the enquiry, in sections: <b>who</b> is enquiring, <b>what</b> it is about, the notes, the documents, who it is assigned to and how it ended up. Each section opens and closes on its own; <b>Expand all</b> or <b>Collapse all</b> does every one.</>,
        },
        {
            target: `[data-tour="${OPEN_SECTION_MARK}"]`,
            badge: 'Left · Sections',
            title: 'Click a section to open it',
            body: <>A section that is closed shows just its title and a summary. <b>Click its title</b> to open it, and again to close it.</>,
        },
        {
            target: DOCUMENTS_TARGET,
            badge: 'Left · Documents',
            title: 'Keep the paperwork with the enquiry',
            body: <>The <b>Documents</b> section holds the files that belong to this enquiry: an email from the enquirer, a signed form, an ID, a photo. <b>Drag files onto the box</b>, or click <b>browse</b> to choose them. Any type of file works, up to 50 files and 15 MB each.</>,
            tip: 'Click a file\'s name to open it, the arrow to download it, or the bin to remove it. New files are kept when you press Save.',
            onEnter: openDocumentsSection,
        },
        ...(canAssign
            ? [{
                target: ASSIGNMENT_TARGET,
                badge: 'Left · Assignment',
                title: 'Assign it to someone',
                body: <>Open the <b>Assignment</b> section to choose who the enquiry is assigned to: pick a person in <b>Assigned To</b>, or change who it is assigned to whenever it needs to move on. <b>Assigned By</b>, the follow-up due date and the reminder are set there too.</>,
                tip: 'Press Save to keep the assignment.',
                onEnter: openAssignmentSection,
            }]
            : []),
        {
            target: '[data-tour="enquiry-save"]',
            badge: 'Left · Save',
            title: 'Save the enquiry\'s details',
            body: <>When you change anything in the details, press <b>Save</b> here to keep it. It becomes available as soon as you change something; hover over it to see why it cannot be pressed.</>,
            tip: 'Follow-ups are saved separately, in their own section on the right.',
        },
        {
            target: FURTHER_DETAILS_TAB,
            badge: 'Middle · Further Details',
            title: 'Further Details',
            body: <>The extra fields your firm sets up for this area of law are on the <b>Further Details</b> tab, beside Activity. Switch to it to see and fill them in.</>,
        },
        {
            target: '[data-tour="enquiry-history"]',
            badge: 'Middle · Activity',
            title: 'What has happened so far',
            body: 'Everything that has happened on this enquiry: the contacts made, the notes and the changes.',
        },
        {
            target: '[data-tour="enquiry-history-filter"]',
            badge: 'Middle · Activity',
            title: 'Filter the history',
            body: <>Only want the calls, the emails and texts, or the follow-ups that are due? Pick a type to show just those. <b>All</b> shows everything again.</>,
        },
        ...(convertButtonLabels.length > 0
            ? [{
                target: '[data-tour="enquiry-convert"]',
                badge: 'Middle · Next step',
                title: 'The next step for this enquiry',
                body: <>When the enquiry is ready, use the button{convertButtonLabels.length > 1 ? 's' : ''} here: {convertButtonLabels.map((label, index) => (
                    <React.Fragment key={label}>{index > 0 ? ', ' : ''}<b>{label}</b></React.Fragment>
                ))}.</>,
            }]
            : []),
        {
            target: '#activity-tab-section-header',
            badge: 'Right · Follow Up',
            title: 'Log what happened, then what is next',
            body: <>Record the contact you just made (<b>what happened</b>), then choose the <b>outcome</b> and schedule the next step.</>,
        },
        {
            target: '[data-tour="enquiry-log-activity"]',
            badge: 'Right · Follow Up',
            title: 'Log activity here',
            body: <>Pick what you did{logLabels.length > 0 ? <>: {joinBold(logLabels)}</> : ''}. It is added to the history when you save the follow-up.</>,
        },
        {
            target: '[data-tour="enquiry-do-something"]',
            badge: 'Right · Follow Up',
            title: 'Or do something here',
            body: <>Instead of only logging, start the next step from here{actionLabels.length > 0 ? <>: {joinBold(actionLabels)}</> : ''}.</>,
            tip: 'Your firm decides which of these are available.',
        },
        {
            target: '[data-tour="enquiry-outcome"]',
            badge: 'Right · Follow Up',
            title: 'Then choose what happens next',
            body: <>Pick the <b>outcome</b>. <b>More Follow Up</b> keeps the enquiry open and lets you schedule the next step. <b>Proceeding</b> means it is moving forward, and you can then create a matter or attach an existing one. <b>Not Proceeding</b> closes it, and asks for a reason.</>,
            tip: 'Only More Follow Up asks for a follow-up date.',
        },
        {
            target: '[data-tour="enquiry-auto-follow-up"]',
            badge: 'Right · Follow Up',
            title: 'Auto Follow Up',
            body: <>Switch on <b>Auto Follow Up</b> and choose an email template below it: at the follow-up date and time the email is sent to the enquirer for you, instead of a reminder going to the person assigned.</>,
            tip: 'It needs an email address on the enquiry, and it only appears for More Follow Up.',
        },
        {
            target: '[data-tour="enquiry-follow-up-save"]',
            badge: 'Right · Save the follow-up',
            title: 'Save the follow-up',
            body: <>A follow-up is saved on its own: press <b>Save follow-up</b> here to log it. <b>Cancel</b> clears what you have typed in this section.</>,
            tip: 'Save follow-up becomes available as soon as you change something in this section.',
        },
        ...(isThirdColumnMatter
            ? [{
                target: '[data-tour="enquiry-column-third"]',
                badge: 'Right · Matter',
                title: 'The matter',
                body: 'This enquiry has become a matter: its matter details are here.',
            }]
            : []),
        {
            badge: 'Done',
            title: 'You are all set',
            body: <>You can replay this any time with the <b>compass</b> button at the top of the form.</>,
        },
    ];

    // the steps either side of the Assignment one put the section back as the tour moves to them (forwards or back)
    const assignmentIndex = steps.findIndex((step) => step.target === ASSIGNMENT_TARGET);
    if (assignmentIndex > -1) {
        [assignmentIndex - 1, assignmentIndex + 1].forEach((neighbourIndex) => {
            const neighbour = steps[neighbourIndex];
            const neighbourOnEnter = neighbour.onEnter;
            neighbour.onEnter = () => {
                restoreAssignmentSection();
                if (neighbourOnEnter) {
                    neighbourOnEnter();
                }
            };
        });
    }

    // and the same for the Documents one
    const documentsIndex = steps.findIndex((step) => step.target === DOCUMENTS_TARGET);
    if (documentsIndex > -1) {
        [documentsIndex - 1, documentsIndex + 1].forEach((neighbourIndex) => {
            const neighbour = steps[neighbourIndex];
            const neighbourOnEnter = neighbour.onEnter;
            neighbour.onEnter = () => {
                restoreDocumentsSection();
                if (neighbourOnEnter) {
                    neighbourOnEnter();
                }
            };
        });
    }
    return steps;
};

interface EnquiryFormTourHostProps {
    // the form raises this each time its tour button is pressed
    tourRequestId: number;
}

// Sits inside the three-column enquiry form. It pops up once on its own the first time the form has loaded (see featureTourState), and again
// whenever the tour button is pressed. The steps are built when it starts, so their wording follows what is on the screen at that moment.
export const EnquiryFormTourHost: React.FC<EnquiryFormTourHostProps> = ({ tourRequestId }) => {
    const [launch, setLaunch] = React.useState<'auto' | 'manual' | null>(null);
    const stepsRef = React.useRef<FeatureTourStep[] | null>(null);
    const hasAutoStartedRef = React.useRef(false);

    React.useEffect(() => {
        if (hasAutoStartedRef.current || !shouldAutoStartFeatureTour(ENQUIRY_FORM_TOUR_ID, ENQUIRY_FORM_TOUR_VERSION)) {
            return undefined;
        }
        // the form's panels arrive a moment after it opens: wait until the first one is on the screen before the tour starts
        let attempts = 0;
        const timer = window.setInterval(() => {
            attempts += 1;
            if (document.querySelector('#contact-details-header') && !isFeatureTourOpen() && !hasAutoStartedRef.current) {
                hasAutoStartedRef.current = true;
                window.clearInterval(timer);
                recordFeatureTourAutoShown(ENQUIRY_FORM_TOUR_ID, ENQUIRY_FORM_TOUR_VERSION);
                setLaunch('auto');
            } else if (attempts >= 12) {
                window.clearInterval(timer);
            }
        }, 800);
        return () => window.clearInterval(timer);
    }, []);

    React.useEffect(() => {
        if (tourRequestId > 0) {
            setLaunch('manual');
        }
    }, [tourRequestId]);

    const onClose = (result: { isCompleted: boolean; isDontShowAgain: boolean }) => {
        clearSectionMark();
        restoreAssignmentSection();
        restoreDocumentsSection();
        stepsRef.current = null;
        recordFeatureTourClosed(ENQUIRY_FORM_TOUR_ID, ENQUIRY_FORM_TOUR_VERSION, result.isCompleted || result.isDontShowAgain);
        setLaunch(null);
    };

    if (!launch) {
        return null;
    }
    if (!stepsRef.current) {
        stepsRef.current = createEnquiryFormTour();
    }
    return <FeatureTour steps={stepsRef.current} showDontShowAgain={launch === 'auto'} onClose={onClose} />;
};
